import { useEffect, useRef } from "react";
import type { SparkController, SparkState } from "../hooks/useSpark.js";
import {
  SPARK_CORE_TINT,
  appearAlpha,
  emberStates,
  flightProgress,
  glowProfile,
  sparkTuning,
  trailSamples,
} from "../lib/spark.js";
import { drawSparkSprite } from "../lib/drawSpark.js";
import styles from "./SparkCanvas.module.css";

/** Resting sprite radius as a share of the shorter viewport side. */
const BASE_RADIUS = 0.22;
/** Sideways drift during the flight, as a share of the viewport width. */
const SWAY = 0.05;

interface Point {
  x: number;
  y: number;
}

/** Where the spark is `elapsedMs` into its flight. */
function sparkPosition(elapsedMs: number, width: number, height: number, radius: number): Point {
  const flight = flightProgress(elapsedMs);
  return {
    x: width / 2 + flight.sway * width * SWAY,
    y: height / 2 - flight.rise * (height / 2 + radius * 2),
  };
}

/** Core tint pushed towards white, as an rgba() string; the show's trail and embers use it. */
function tintColor(white: number, alpha: number): string {
  const { r, g, b } = SPARK_CORE_TINT;
  const mix = (c: number): number => Math.round(c + (255 - c) * white);
  return `rgba(${mix(r)}, ${mix(g)}, ${mix(b)}, ${alpha.toFixed(4)})`;
}

/** Additive streak along the flown path, a little whiter and wider towards the head. */
function drawTrail(
  ctx: CanvasRenderingContext2D,
  elapsedMs: number,
  width: number,
  height: number,
  radius: number,
): void {
  const samples = trailSamples(elapsedMs);
  const points = samples.map((s) => sparkPosition(s.elapsedMs, width, height, radius));
  const { trailWidth, trailTaper, trailGlow, trailCore } = sparkTuning;
  const coreWidth = radius * trailWidth;
  // A wide faint pass under a narrow bright one stands in for the shader's soft edge.
  const passes = [
    { widthScale: 2.2, alpha: trailGlow },
    { widthScale: 1, alpha: trailCore },
  ];
  ctx.lineCap = "butt";
  for (const pass of passes) {
    for (let k = 0; k < samples.length - 1; k++) {
      const front = samples[k]!;
      const back = samples[k + 1]!;
      const fade = (front.fade + back.fade) / 2;
      if (fade <= 0.001) continue;
      const head = (front.head + back.head) / 2;
      // Same lift towards white as trail.frag.glsl.
      ctx.strokeStyle = tintColor(0.1 + 0.15 * head, fade * pass.alpha);
      ctx.lineWidth = coreWidth * pass.widthScale * (trailTaper + (1 - trailTaper) * head);
      ctx.beginPath();
      ctx.moveTo(points[k]!.x, points[k]!.y);
      ctx.lineTo(points[k + 1]!.x, points[k + 1]!.y);
      ctx.stroke();
    }
  }
}

function drawEmbers(
  ctx: CanvasRenderingContext2D,
  elapsedMs: number,
  seed: number,
  width: number,
  height: number,
  radius: number,
): void {
  for (const ember of emberStates(elapsedMs, seed)) {
    const at = sparkPosition(ember.elapsedMs, width, height, radius);
    const x = at.x + ember.dx * radius;
    const y = at.y + ember.dy * radius;
    const r = radius * sparkTuning.emberSize * ember.size;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, tintColor(0.6, ember.alpha));
    gradient.addColorStop(1, tintColor(0.6, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
}

function draw(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: Readonly<SparkState>,
  now: number,
  reducedMotion: boolean,
): void {
  ctx.clearRect(0, 0, width, height);

  const profile = glowProfile(state.charge, now);
  const radius = Math.min(width, height) * BASE_RADIUS * profile.radius;
  let alpha = appearAlpha(now - state.appearStart);
  let x = width / 2;
  let y = height / 2;
  let stretch = 1;

  if (state.flightStart !== null) {
    const elapsed = now - state.flightStart;
    const flight = flightProgress(elapsed);
    if (reducedMotion) {
      alpha *= 1 - flight.t;
    } else {
      ({ x, y } = sparkPosition(elapsed, width, height, radius));
      stretch = 1 + 0.6 * flight.t * flight.t;

      // Under the spark, so its halo sits on the trail's head.
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      drawTrail(ctx, elapsed, width, height, radius);
      drawEmbers(ctx, elapsed, state.flightStart, width, height, radius);
      ctx.restore();
    }
  }
  drawSparkSprite(ctx, x, y, radius, profile, alpha, stretch);
}

/** Full-screen background canvas showing the visitor's spark. */
export function SparkCanvas({ spark }: { spark: SparkController }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let width = 0;
    let height = 0;

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const unsubscribe = spark.onFrame((state, now) =>
      draw(ctx, width, height, state, now, motionQuery.matches),
    );
    return () => {
      unsubscribe();
      window.removeEventListener("resize", resize);
    };
  }, [spark]);

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas} ${styles.overScreen}`}
      aria-hidden="true"
    />
  );
}
